import { createColumnHelper } from '@tanstack/react-table'
import type { ParseKeys } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import { RecordId } from '@/components/data/DataTable'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { toHiddenColumns, toVisibilityState, useViewState } from '@/components/data/useViewState'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { ClientStatement, Money, StatementRow } from '@/types/domain'

/* One client's yearly statement — the workbook's per-customer sheet. Rows are
 * cases oldest first and are NOT sortable: `running` is a balance carried down
 * the page, and a column sort would print a cumulative figure in an order it
 * was not accumulated in. Columns are hideable like everywhere else.
 *
 * The opening line and the total line are not rows of the list: they are a
 * strip above and a strip below, so neither the virtualiser nor a hidden
 * column can move or drop them. Money columns exist only when the server sent
 * `opening`/`totals` — the page's reading of `moneyUnavailable`. */

const column = createColumnHelper<StatementRow>()

/** One entry of the columns menu, before it is translated. */
interface MenuColumn {
  id: string
  labelKey: ParseKeys
  locked?: boolean
}

const BASE_COLUMNS: readonly MenuColumn[] = [
  { id: 'openedOn', labelKey: 'clients.statement.columns.openedOn' },
  { id: 'number', labelKey: 'clients.statement.columns.number', locked: true },
  { id: 'route', labelKey: 'clients.statement.columns.route' },
  { id: 'mode', labelKey: 'clients.statement.columns.mode' },
  { id: 'cargo', labelKey: 'clients.statement.columns.cargo' },
  { id: 'weightKg', labelKey: 'clients.statement.columns.weight' },
  { id: 'volumeM3', labelKey: 'clients.statement.columns.volume' },
]
const MONEY_COLUMNS: readonly MenuColumn[] = [
  { id: 'billed', labelKey: 'clients.statement.columns.billed' },
  { id: 'received', labelKey: 'clients.statement.columns.received' },
  // S-22f: money paid back to the client — it does not move «Получено».
  { id: 'refunded', labelKey: 'clients.statement.columns.refunded' },
  { id: 'running', labelKey: 'clients.statement.columns.running' },
  { id: 'lastReceivedOn', labelKey: 'clients.statement.columns.lastReceivedOn' },
  { id: 'cost', labelKey: 'clients.statement.columns.cost' },
]
const TAIL_COLUMNS: readonly MenuColumn[] = [
  { id: 'paymentMethod', labelKey: 'clients.statement.columns.paymentMethod' },
  { id: 'ata', labelKey: 'clients.statement.columns.ata' },
  { id: 'transitDays', labelKey: 'clients.statement.columns.transitDays' },
  { id: 'owner', labelKey: 'clients.statement.columns.owner' },
  { id: 'status', labelKey: 'clients.statement.columns.status' },
]
const LOCKED: readonly string[] = ['number']

const STRIP_ROW = 'border-line-hair flex items-center justify-between gap-4 border-b px-[18px] text-[13px]'

function routeText(row: StatementRow): string {
  return [row.route.loading, row.route.border, row.route.discharge, row.route.unloading]
    .filter((part) => part !== '')
    .join(' → ')
}

export function ClientStatementTable({
  rows,
  opening,
  totals,
  onOpenCase,
}: {
  rows: StatementRow[]
  opening?: Money
  totals?: ClientStatement['totals']
  onOpenCase: (row: StatementRow) => void
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const [viewState, controls] = useViewState('clients.statement')
  const showMoney = opening !== undefined && totals !== undefined

  const menu = useMemo(
    () =>
      [...BASE_COLUMNS, ...(showMoney ? MONEY_COLUMNS : []), ...TAIL_COLUMNS].map((entry) => ({
        id: entry.id,
        label: t(entry.labelKey),
        locked: entry.locked,
      })),
    [showMoney, t],
  )
  const columnVisibility = useMemo(
    () => toVisibilityState(viewState.hiddenColumns.filter((id) => !LOCKED.includes(id))),
    [viewState.hiddenColumns],
  )

  const moneyCell = (value: string | undefined, emphasis = false) =>
    value === undefined ? (
      t('common.empty')
    ) : (
      <span className={cn(emphasis && 'font-semibold', emphasis && Number(value) < 0 && 'text-negative')}>
        {formatNumber(value, 2)}
      </span>
    )

  const columns = useMemo(
    () => [
      column.accessor('openedOn', {
        header: t('clients.statement.columns.openedOn'),
        size: 110,
        cell: (info) => format.date(info.getValue()),
      }),
      column.accessor('number', {
        header: t('clients.statement.columns.number'),
        size: 120,
        cell: (info) => <RecordId>{info.getValue()}</RecordId>,
      }),
      column.accessor(routeText, {
        id: 'route',
        header: t('clients.statement.columns.route'),
        cell: (info) => info.getValue() || t('common.empty'),
      }),
      column.accessor('transportModeCode', {
        id: 'mode',
        header: t('clients.statement.columns.mode'),
        size: 110,
        cell: (info) => info.getValue() || t('common.empty'),
      }),
      column.accessor('cargoDescription', {
        id: 'cargo',
        header: t('clients.statement.columns.cargo'),
        cell: (info) => info.getValue() || t('common.empty'),
      }),
      column.accessor((row) => row.weightKg ?? '', {
        id: 'weightKg',
        header: t('clients.statement.columns.weight'),
        size: 110,
        meta: { numeric: true },
        cell: (info) => (info.getValue() === '' ? t('common.empty') : formatNumber(info.getValue(), 0)),
      }),
      column.accessor((row) => row.volumeM3 ?? '', {
        id: 'volumeM3',
        header: t('clients.statement.columns.volume'),
        size: 100,
        meta: { numeric: true },
        cell: (info) => (info.getValue() === '' ? t('common.empty') : formatNumber(info.getValue(), 1)),
      }),
      ...(showMoney
        ? [
            column.accessor((row) => row.money?.billed.amount, {
              id: 'billed',
              header: t('clients.statement.columns.billed'),
              size: 120,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue()),
            }),
            column.accessor((row) => row.money?.received.amount, {
              id: 'received',
              header: t('clients.statement.columns.received'),
              size: 120,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue()),
            }),
            column.accessor((row) => row.money?.refunded.amount, {
              id: 'refunded',
              header: t('clients.statement.columns.refunded'),
              size: 120,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue()),
            }),
            column.accessor((row) => row.money?.running.amount, {
              id: 'running',
              header: t('clients.statement.columns.running'),
              size: 120,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue(), true),
            }),
            column.accessor((row) => row.money?.lastReceivedOn, {
              id: 'lastReceivedOn',
              header: t('clients.statement.columns.lastReceivedOn'),
              size: 110,
              cell: (info) => format.date(info.getValue()),
            }),
            column.accessor((row) => row.money?.cost.amount, {
              id: 'cost',
              header: t('clients.statement.columns.cost'),
              size: 120,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue()),
            }),
          ]
        : []),
      column.accessor('paymentMethod', {
        header: t('clients.statement.columns.paymentMethod'),
        size: 100,
        // Through the shared `cases.method.*` catalogue, like the case
        // overview: the wire spelling is a code, not a word for a reader.
        cell: (info) => {
          const method = info.getValue()
          return method ? t(`cases.method.${method}`) : t('common.empty')
        },
      }),
      column.accessor((row) => row.ata, {
        id: 'ata',
        header: t('clients.statement.columns.ata'),
        size: 110,
        cell: (info) => format.date(info.getValue()),
      }),
      column.accessor((row) => row.transitDays, {
        id: 'transitDays',
        header: t('clients.statement.columns.transitDays'),
        size: 72,
        meta: { numeric: true },
        cell: (info) => (info.getValue() === undefined ? t('common.empty') : String(info.getValue())),
      }),
      column.accessor('ownerName', {
        id: 'owner',
        header: t('clients.statement.columns.owner'),
        cell: (info) => info.getValue() || t('common.empty'),
      }),
      column.accessor('status', {
        header: t('clients.statement.columns.status'),
        size: 120,
        cell: (info) => <CaseStatusPill status={info.getValue()} />,
      }),
    ],
    // moneyCell closes over t only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, format, showMoney],
  )

  return (
    <>
      <div className="flex items-center justify-end gap-1.5 px-3.5 pb-2.5">
        <ColumnsMenu
          columns={menu}
          hidden={viewState.hiddenColumns}
          onToggle={controls.toggleColumn}
          onReset={() => controls.setHiddenColumns([])}
        />
      </div>

      {showMoney ? (
        <div className={STRIP_ROW} data-testid="statement-opening">
          <span className="text-text-muted">{t('clients.statement.openingRow')}</span>
          <span className="font-semibold tabular-nums">{formatNumber(opening.amount, 2)}</span>
        </div>
      ) : null}

      <RecordList
        label={t('clients.statement.title')}
        data={rows}
        columns={columns}
        getRowId={(row) => row.caseId}
        onRowClick={onOpenCase}
        renderCard={(row) => (
          <RecordCard
            id={row.number}
            status={<CaseStatusPill status={row.status} />}
            primary={routeText(row) || row.cargoDescription}
            secondary={format.date(row.openedOn)}
            value={row.money ? formatNumber(row.money.running.amount, 2) : undefined}
            valueLabel={row.money ? t('clients.statement.columns.running') : undefined}
          />
        )}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={(next) => controls.setHiddenColumns(toHiddenColumns(next))}
      />

      {showMoney ? (
        <div className={cn(STRIP_ROW, 'border-b-0 border-t')} data-testid="statement-total">
          <span className="text-text-heading font-semibold">{t('clients.statement.totalRow')}</span>
          <span className="flex gap-6 tabular-nums">
            <span>{formatNumber(totals.billed.amount, 2)}</span>
            <span>{formatNumber(totals.received.amount, 2)}</span>
            <span>{formatNumber(totals.refunded.amount, 2)}</span>
            <span className="font-semibold">{formatNumber(totals.balance.amount, 2)}</span>
            <span className="text-text-muted">{formatNumber(totals.cost.amount, 2)}</span>
          </span>
        </div>
      ) : null}
    </>
  )
}
